import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgCurrency } from '../../../../hooks/useOrgCurrency.js';
import { CheckCircle, Car, Euro, Shuffle, Shield, Building2, Lock, Unlock, FileText, Truck, Package, Calendar } from 'lucide-react';

/**
 * Contract type i18n keys (values match insurance workflow's contract types)
 */
const CONTRACT_TYPE_KEYS = [
  'fleet_auto', 'individual_auto', 'fleet_breakdown', 'individual_breakdown',
  'fleet_deductible', 'individual_deductible', 'fleet_other', 'individual_other',
];

/**
 * Guarantee i18n keys (legacy coverage codes)
 */
const GUARANTEE_KEYS = [
  'rc', 'damage_collision', 'damage_all', 'theft_fire', 'glass',
  'assistance', 'replacement', 'driver', 'natural', 'legal',
];

/**
 * StepSummary - Step 7 (final) of VehicleLifecycleWorkflow
 *
 * Displays a read-only summary of all data entered in previous steps:
 * - Identification (make, model, VIN, license plate, fuel type)
 * - Owner (tenant or external, legal status)
 * - Financing (acquisition type, price, payment)
 * - Assignments (organization, driver, location, status)
 * - Insurance (contract selection)
 *
 * @param {Object} props
 * @param {import('../VehicleLifecycleWorkflow').VehicleFormData} props.formData - Current form state
 * @param {Array<{code: string, name: string}>} props.organizations - Available organizations
 * @param {Array<{id: number, first_name: string, last_name: string}>} props.drivers - All drivers
 * @param {Array<{id: number, site_name: string}>} props.sites - All sites
 * @param {Object} props.tenant - Tenant data from AuthContext
 * @returns {JSX.Element}
 */
function StepSummary({ formData, organizations, drivers, sites, tenant, pendingScanFile }) {
  const { t } = useTranslation();
  // Devise de la filiale active - un seul outil monetaire.
  const { moneyScope } = useOrgCurrency();
  const driverId = formData.assigned_driver_id ? parseInt(formData.assigned_driver_id) : null;
  const siteId = formData.initial_location_id ? parseInt(formData.initial_location_id) : null;
  const selectedDriver = driverId
    ? drivers.find(d => d.id === driverId)
    : null;
  const selectedSite = siteId
    ? sites.find(s => s.id === siteId)
    : null;
  const selectedOrg = organizations.find(o => o.code === formData.org_id);

  // Parse insurance assignments
  const insuranceAssignments = useMemo(() => {
    try {
      return JSON.parse(formData.insurance_assignments || '[]');
    } catch {
      return [];
    }
  }, [formData.insurance_assignments]);

  // Parse guarantees (legacy)
  const selectedGuarantees = useMemo(() => {
    try {
      return JSON.parse(formData.insurance_guarantees || '["rc"]');
    } catch {
      return ['rc'];
    }
  }, [formData.insurance_guarantees]);

  // Parse coverage model selection (new system)
  const coverageModelInfo = useMemo(() => {
    try {
      return JSON.parse(formData.insurance_coverage_model || '{}');
    } catch {
      return {};
    }
  }, [formData.insurance_coverage_model]);

  // Get owner display name
  const getOwnerName = () => {
    if (formData.owner_type === 'tenant') {
      return tenant?.billing_name || tenant?.name || t('vehicles.wizard.summary.ownerCompanyDefault');
    }
    return formData.owner_name || t('vehicles.wizard.summary.ownerNameDefault');
  };

  const fuelLabel = t(`vehicles.fuelType.${formData.fuel_type}`, { defaultValue: formData.fuel_type });
  const statusLabel = t(`vehicles.status.${formData.status}`, { defaultValue: t('vehicles.wizard.summary.statusInactive') });

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('vehicles.wizard.summary.title')}</h3>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold text-green-900 mb-2">{t('vehicles.wizard.summary.headerTitle')}</h4>
            <p className="text-sm text-green-800">
              {t('vehicles.wizard.summary.headerHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Identification Summary */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Car className="w-5 h-5 text-amber-600" />
            {t('vehicles.wizard.summary.sections.identification')}
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.fields.make')}</span>
              <span className="font-medium">{formData.make || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.fields.model')}</span>
              <span className="font-medium">{formData.model || '-'}</span>
            </div>
            {formData.version && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('vehicles.wizard.summary.fields.version')}</span>
                <span className="font-medium">{formData.version}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.fields.year')}</span>
              <span className="font-medium">{formData.year}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.fields.plate')}</span>
              <span className="font-medium">{formData.license_plate || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.fields.vin')}</span>
              <span className="font-medium text-xs">{formData.vin || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.fields.fuel')}</span>
              <span className="font-medium">{fuelLabel}</span>
            </div>
            {/* Reception Status */}
            <div className="pt-2 mt-2 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('vehicles.wizard.summary.fields.status')}</span>
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  formData.reception_status === 'ordered'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {formData.reception_status === 'ordered' ? (
                    <><Package className="w-3 h-3" /> {t('vehicles.wizard.summary.receptionOrdered')}</>
                  ) : (
                    <><CheckCircle className="w-3 h-3" /> {t('vehicles.wizard.summary.receptionReceived')}</>
                  )}
                </span>
              </div>
              {formData.reception_status === 'ordered' ? (
                <>
                  {formData.order_date && (
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600">{t('vehicles.wizard.summary.orderDate')}</span>
                      <span className="font-medium">{formData.order_date}</span>
                    </div>
                  )}
                  {formData.expected_delivery_date && (
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600">{t('vehicles.wizard.summary.expectedDelivery')}</span>
                      <span className="font-medium text-amber-600">{formData.expected_delivery_date}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex justify-between mt-1">
                  <span className="text-gray-600">{t('vehicles.wizard.summary.receptionDate')}</span>
                  <span className="font-medium">{formData.entry_date || '-'}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Owner Summary */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            {t('vehicles.wizard.summary.sections.owner')}
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.ownerType')}</span>
              <span className="font-medium">
                {formData.owner_type === 'tenant' ? t('vehicles.wizard.summary.ownerTypeTenant') : t('vehicles.wizard.summary.ownerTypeExternal')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.company')}</span>
              <span className="font-medium">{getOwnerName()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('vehicles.wizard.summary.legalStatus')}</span>
              <span className={`font-medium flex items-center gap-1 px-2 py-1 rounded text-xs ${
                formData.is_mortgaged ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
              }`}>
                {formData.is_mortgaged ? (
                  <><Lock className="w-3 h-3" /> {t('vehicles.wizard.summary.mortgaged')}</>
                ) : (
                  <><Unlock className="w-3 h-3" /> {t('vehicles.wizard.summary.notMortgaged')}</>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Financing Summary */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Euro className="w-5 h-5 text-green-600" />
            {t('vehicles.wizard.summary.sections.financing')}
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.financingType')}</span>
              <span className={`font-medium px-2 py-0.5 rounded text-xs ${
                formData.acquisition_type === 'cash' ? 'bg-green-100 text-green-800' :
                formData.acquisition_type === 'lease' ? 'bg-amber-100 text-amber-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {formData.acquisition_type === 'cash' ? t('vehicles.wizard.summary.financingTypeCash') :
                 formData.acquisition_type === 'lease' ? t('vehicles.wizard.summary.financingTypeLease') : t('vehicles.wizard.summary.financingTypeCredit')}
              </span>
            </div>
            {formData.acquisition_type === 'cash' && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('vehicles.wizard.summary.purchasePriceExcl')}</span>
                  <span className="font-medium">{moneyScope(formData.purchase_price || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('vehicles.wizard.summary.purchaseDate')}</span>
                  <span className="font-medium">{formData.purchase_date || '-'}</span>
                </div>
              </>
            )}
            {formData.acquisition_type === 'loan' && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('vehicles.wizard.summary.financedAmount')}</span>
                  <span className="font-medium">{moneyScope(formData.financed_amount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('vehicles.wizard.summary.monthlyPayment')}</span>
                  <span className="font-medium">{moneyScope(formData.monthly_payment || 0)}</span>
                </div>
                {formData.interest_rate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('vehicles.wizard.summary.apr')}</span>
                    <span className="font-medium">{formData.interest_rate}%</span>
                  </div>
                )}
              </>
            )}
            {formData.acquisition_type === 'lease' && (() => {
              const baseMonthly = parseFloat(formData.monthly_payment) || 0;
              const optionsHT = parseFloat(formData.financing_options_cost_ht) || 0;
              const totalMonthlyHT = baseMonthly + optionsHT;
              return (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('vehicles.wizard.summary.leaseType')}</span>
                    <span className="font-medium">{(formData.financing_type || 'lld').toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('vehicles.wizard.summary.duration')}</span>
                    <span className="font-medium">{t('vehicles.wizard.summary.months', { count: formData.lease_duration_months || 0 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('vehicles.wizard.summary.vehicleRent')}</span>
                    <span className="font-medium">{moneyScope(baseMonthly)}</span>
                  </div>
                  {optionsHT > 0 && (
                    <>
                      <div className="flex justify-between text-[13px]">
                        <span className="text-gray-500">{t('vehicles.wizard.summary.includedOptions')}</span>
                        <span className="font-medium text-gray-700">{moneyScope(optionsHT)}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-gray-200">
                        <span className="text-gray-900 font-semibold">{t('vehicles.wizard.summary.totalMonthly')}</span>
                        <span className="font-bold text-gray-900">{moneyScope(totalMonthlyHT)}</span>
                      </div>
                    </>
                  )}
                  {formData.lease_first_payment && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('vehicles.wizard.summary.firstPayment')}</span>
                      <span className="font-medium">{moneyScope(formData.lease_first_payment)}</span>
                    </div>
                  )}
                  {formData.lease_deposit && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('vehicles.wizard.summary.deposit')}</span>
                      <span className="font-medium">{moneyScope(formData.lease_deposit)}</span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Assignments Summary */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Shuffle className="w-5 h-5 text-purple-600" />
            {t('vehicles.wizard.summary.sections.assignments')}
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.organization')}</span>
              <span className="font-medium">{selectedOrg?.name || formData.org_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.driver')}</span>
              <span className="font-medium">
                {selectedDriver
                  ? `${selectedDriver.first_name} ${selectedDriver.last_name}`
                  : t('vehicles.wizard.summary.driverUnassigned')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.location')}</span>
              <span className="font-medium">
                {selectedSite?.site_name || t('vehicles.wizard.summary.locationUndefined')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('vehicles.wizard.summary.fields.status')}</span>
              <span className={`font-medium px-2 py-1 rounded text-xs ${
                formData.status === 'active' ? 'bg-green-100 text-green-800' :
                formData.status === 'maintenance' ? 'bg-amber-100 text-amber-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {statusLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Insurance Summary */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            {t('vehicles.wizard.summary.sections.insurance')}
          </h4>
          <div className="space-y-2 text-sm">
            {insuranceAssignments.length > 0 ? (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('vehicles.wizard.summary.contracts')}</span>
                  <span className="font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded">
                    {insuranceAssignments.length}
                  </span>
                </div>
                <div className="space-y-1">
                  {insuranceAssignments.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <span className="text-green-600">•</span>
                      <span className="font-medium">
                        {CONTRACT_TYPE_KEYS.includes(a.type) ? t(`vehicles.wizard.summary.contractTypes.${a.type}`) : a.type}
                      </span>
                      {a.existingContractId && (
                        <span className="text-gray-400">(ID: {a.existingContractId})</span>
                      )}
                      {a.createNew && (
                        <span className="bg-amber-100 text-amber-700 px-1 rounded text-xs">{t('vehicles.wizard.summary.newBadge')}</span>
                      )}
                    </div>
                  ))}
                </div>
                {coverageModelInfo.coverageModelId ? (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                      {coverageModelInfo.coverageModelName || t('vehicles.wizard.summary.coverageSelected')}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-gray-100">
                    {selectedGuarantees.map(g => (
                      <span key={g} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                        {GUARANTEE_KEYS.includes(g) ? t(`vehicles.wizard.summary.guarantees.${g}`) : g}
                      </span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-center py-2">{t('vehicles.wizard.summary.noCoverage')}</p>
            )}
          </div>
        </div>

        {/* Documents Summary */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            {t('vehicles.wizard.summary.sections.documents')}
          </h4>
          <div className="space-y-2 text-sm">
            {pendingScanFile && (
              <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 p-2 rounded">
                <span className="text-green-600 font-bold">&#10003;</span>
                <span>{t('vehicles.wizard.summary.registrationCard')}</span>
                <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">{t('vehicles.wizard.summary.scanned')}</span>
                <span className="text-gray-400">({pendingScanFile.file?.name})</span>
              </div>
            )}
            {formData.pendingDocuments && formData.pendingDocuments.length > 0 ? (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('vehicles.wizard.summary.pendingDocuments')}</span>
                  <span className="font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    {formData.pendingDocuments.length}
                  </span>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  {formData.pendingDocuments.map((doc, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-amber-600">•</span>
                      <span>{doc.documentLabel}</span>
                      <span className="text-gray-400">({doc.fileName})</span>
                    </div>
                  ))}
                </div>
              </>
            ) : !pendingScanFile ? (
              <p className="text-gray-500 text-center py-2">{t('vehicles.wizard.summary.noDocuments')}</p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Ready to Submit */}
      <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6 text-center">
        <CheckCircle className="w-16 h-16 text-amber-600 mx-auto mb-4" />
        <h4 className="text-lg font-bold text-amber-900 mb-2">{t('vehicles.wizard.summary.readyTitle')}</h4>
        <p className="text-sm text-amber-800">
          {t('vehicles.wizard.summary.readyHint')}
        </p>
      </div>
    </div>
  );
}

export default StepSummary;
